import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createHouse, getHouseById, updateHouse } from '../../services/houseService';
import { House } from '../../types/models';
import { ArrowLeft, Save } from 'lucide-react';

const HouseForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState<Omit<House, 'house_id' | 'created_at'>>({
    house_name: '',
    house_color: '#000000'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHouse = async () => {
      if (id) {
        try {
          const data = await getHouseById(id);
          if (data) {
            setFormData({
              house_name: data.house_name,
              house_color: data.house_color
            });
          }
        } catch (error) {
          console.error('Error fetching house:', error);
          setError('Failed to load house data');
        }
      }
    };

    if (isEditMode) {
      fetchHouse();
    }
  }, [id, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      if (isEditMode && id) {
        await updateHouse(id, formData);
      } else {
        await createHouse(formData);
      }
      
      navigate('/houses');
    } catch (error: any) {
      console.error('Error saving house:', error);
      setError(error.message || 'Failed to save house');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? t('houses.form.editTitle') : t('houses.form.addTitle')}
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label htmlFor="house_name" className="block text-sm font-medium text-gray-700">
                {t('houses.form.fields.houseName.label')}
              </label>
              <input
                type="text"
                name="house_name"
                id="house_name"
                required
                value={formData.house_name}
                onChange={handleChange}
                placeholder={t('houses.form.fields.houseName.placeholder')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="house_color" className="block text-sm font-medium text-gray-700">
                {t('houses.form.fields.houseColor.label')}
              </label>
              <div className="mt-1 flex items-center space-x-3">
                <input
                  type="color"
                  name="house_color"
                  id="house_color"
                  required
                  value={formData.house_color}
                  onChange={handleChange}
                  className="h-10 w-20 border border-gray-300 rounded-md shadow-sm p-1"
                />
                <input
                  type="text"
                  name="house_color"
                  value={formData.house_color}
                  onChange={handleChange}
                  placeholder={t('houses.form.fields.houseColor.placeholder')}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/houses')}
              className="mr-3 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('houses.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? t('houses.actions.saving') : t('houses.actions.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HouseForm;